import React, { useState } from 'react';
import { cx } from '@twind/core';

import { type FAQItem, FAQTypeValue, type SuggestedEntry } from '../../../copied/models/ChatModules';
import { waitForIDs } from '../../../copied/getIds';
import VideoPlayer from '../../../components/VideoPlayer';
import { Heading1 } from '../../../components/Typography';
import TailorDynamicContent from '../../../components/OstroDynamicContent';
import useTailorTracking from '../hooks/useTailorTracking';
import useLinks from '../hooks/useLinks';
import TailorFaqButton from './TailorFaqButton';
import { useSearch } from '../hooks/useSearch';

type CaptureLinkClickType = (type: 'link' | 'button', href?: string, ctaCopy?: string | null) => void;

function TailorArticleView({ article }: { article: SuggestedEntry }) {
  const { title, custom_title, render_json = [] } = article;
  const { captureArticleLinkClick } = useTailorTracking();
  const { search } = useSearch();

  const captureLinkClick: CaptureLinkClickType = (type, href, ctaCopy) => {
    waitForIDs().then(({ conditionId }) => {
      captureArticleLinkClick({
        conditionId,
        ctaCopy,
        search,
        title,
        href,
        type,
      });
    });
  };

  return (
    <div>
      {custom_title ? (
        <TailorDynamicContent
          className="mt-9 mb-4"
          content={title}
        />
      ) : (
        <Heading1 className="mt-9 mb-4">{title}</Heading1>
      )}

      <div id="ostro-tailor-article-pieces-container">
        {render_json?.map((item: FAQItem, index: number) => (
          <ArticlePiece
            key={index}
            title={title}
            item={item}
            captureLinkClick={captureLinkClick}
          />
        ))}
      </div>
    </div>
  );
}

export default TailorArticleView;

function ArticlePiece({
  item,
  title,
  captureLinkClick,
}: {
  item: FAQItem;
  title?: string;
  captureLinkClick: CaptureLinkClickType;
}) {
  const [playing, setPlaying] = useState<string | undefined>();
  const { openLink, getLinkTarget } = useLinks();
  const handleLinkClick = (e: React.MouseEvent<HTMLElement>, type: 'link' | 'button' = 'link') => {
    e.preventDefault();
    const target = e.target as HTMLElement;
    const href = target?.closest('a')?.href;
    const ctaCopy = target?.closest('a')?.textContent;

    if (!href) return;

    captureLinkClick(type, href, ctaCopy);
    openLink(href);
  };

  switch (item?.reg_id?._type_val) {
    case FAQTypeValue.Text:
      return (
        <TailorDynamicContent
          onClick={handleLinkClick}
          content={item?.body || ''}
        />
      );
    case FAQTypeValue.Image: {
      const maxWidthClass = `max-w-[${item.scale || 100}%]`;
      const wrapperStyle = {
        display: 'flex',
        justifyContent: item.alignment || 'center',
      };

      return item.link_url ? (
        <a
          style={wrapperStyle}
          href={item.link_url}
          target={getLinkTarget(item.link_url)}
          rel="noreferrer"
          onClick={(e) => handleLinkClick(e, 'link')}
          className="flex mb-4"
        >
          <img
            className={maxWidthClass}
            src={item?.url}
            alt="FAQ"
          />
        </a>
      ) : (
        <div style={wrapperStyle}>
          <img
            className={cx(maxWidthClass, 'mb-4')}
            src={item?.url}
            alt="FAQ"
          />
        </div>
      );
    }
    case FAQTypeValue.Video:
      return (
        <VideoPlayer
          title={title}
          video_name={item.video_name}
          url={item.video_url}
          controls
          muted
          width="100%"
          height="180px"
          className="mb-4"
          light={item.placeholder_url}
          playing={playing === item.video_url}
          onClickPreview={(e) => {
            e.stopPropagation();
            setPlaying(item.video_url);
          }}
        />
      );
    case FAQTypeValue.HelpBlock:
      return null;
    case FAQTypeValue.Button:
      return (
        <div className="mb-4 ostro-faq-button-element">
          <TailorFaqButton
            href={item.redirect_link || ''}
            target={getLinkTarget(item.redirect_link || '')}
            onClick={(e) => handleLinkClick(e, 'button')}
            buttonStyle={item.button_style}
            iconEnabled={item.icon_enabled}
            iconUrl={item.icon_url}
            buttonText={item.button_text || ''}
          />
        </div>
      );
    default:
      return null;
  }
}
